import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactNode, useEffect } from 'react';
import { PermissionGuard } from './components/PermissionGuard';
import { AuthError } from './components/AuthError';
import { AdminUserDetailPage } from './pages/AdminUserDetailPage';
import { ProfilePage } from './pages/ProfilePage';
import './App.css';
import { P } from '@wevo/wevo-access';
import { OrganizationTeamsView } from './components/OrganizationTeamsView';
import { SsoAdmin } from './components/SsoAdmin';
import { useForceLogoutOnLoginParams } from './hooks/useForceLogoutOnLoginParams';
import { usePortalMode } from './hooks/usePortalMode';
import { LoadingPage } from './components/LoadingSpinner';
import { AdminShell } from './components/admin/AdminShell';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminIdentityAttention } from './components/admin/AdminIdentityAttention';
import { CompanyDetail } from './components/admin/credit/CompanyDetail';
import { SowCreditDetail } from './components/admin/credit/SowCreditDetail';
import { PricingConfigDetail } from './components/admin/credit/PricingConfigDetail';
import { buildInviteLoginAuthorizationParams } from './lib/invite-login-params';

function DefaultPortalRedirect() {
    const { defaultPath } = usePortalMode();

    return <Navigate to={defaultPath} replace />;
}

function AdminRoute({ children, permissions }: { children: ReactNode; permissions?: string[] }) {
    return (
        <PermissionGuard
            permissions={permissions || [P.WEVO.ACCESS.API.ADMINISTRATE_ORGANIZATIONS]}
        >
            <AdminShell>{children}</AdminShell>
        </PermissionGuard>
    );
}

function App() {
    const { isAuthenticated, isLoading, loginWithRedirect, error, logout } = useAuth0();

    // Force logout if URL parameters indicate login with different user
    useForceLogoutOnLoginParams({
        debug: process.env.NODE_ENV === 'development',
        clearParamsAfterLogout: false, // Keep params for after logout redirect
    });

    // Automatically redirect to login when not authenticated
    useEffect(() => {
        if (!isLoading && !isAuthenticated && !error) {
            const authorizationParams = buildInviteLoginAuthorizationParams(window.location.search);
            const hasInviteParams = Object.keys(authorizationParams).length > 0;
            loginWithRedirect(hasInviteParams ? { authorizationParams } : undefined);
        }
    }, [isLoading, isAuthenticated, loginWithRedirect, error]);

    if (isLoading) {
        return <LoadingPage message="Checking your session..." />;
    }

    if (!isAuthenticated && error) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-admin-app p-4">
                <div className="w-full max-w-md rounded-xl border bg-card p-8 shadow-lg">
                    <div className="mb-6 flex justify-center">
                        <img src="/Horizontal-FullColor-1.svg" alt="WEVO" className="h-6 w-auto" />
                    </div>
                    <div className="space-y-6 text-center">
                        <div>
                            <h1 className="text-xl font-semibold text-neutral-900">
                                Unable to sign you in
                            </h1>
                            <p className="mt-2 text-sm text-neutral-600">
                                The admin portal needs a valid session before it can load.
                            </p>
                        </div>
                        <AuthError />
                        <button
                            type="button"
                            onClick={() => loginWithRedirect()}
                            className="w-full rounded-md bg-[#3B6CAB] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#31598f]"
                        >
                            Try again
                        </button>
                        <button
                            type="button"
                            onClick={() => logout()}
                            className="w-full rounded-md border border-[#3B6CAB] px-4 py-2.5 text-sm font-medium text-[#3B6CAB] transition-colors hover:bg-[#f4f8fc]"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <LoadingPage message="Redirecting to sign in..." />;
    }

    return (
        <Router>
            <Routes>
                <Route path="/" element={<DefaultPortalRedirect />} />
                <Route
                    path="/admin"
                    element={
                        <AdminRoute>
                            <AdminDashboard />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/companies"
                    element={<Navigate to="/admin?tab=companies&browse=1" replace />}
                />
                <Route
                    path="/admin/companies/:companyId"
                    element={
                        <AdminRoute>
                            <CompanyDetail />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/companies/:companyId/sso"
                    element={
                        <AdminRoute>
                            <SsoAdmin />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/pricing-configs/:pricingConfigId"
                    element={
                        <AdminRoute>
                            <PricingConfigDetail />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/sows/:sowId"
                    element={
                        <AdminRoute>
                            <SowCreditDetail />
                        </AdminRoute>
                    }
                />
                <Route path="/admin/agreements" element={<Navigate to="/admin" replace />} />
                <Route
                    path="/admin/attention"
                    element={
                        <AdminRoute permissions={[P.WEVO.ACCESS.API.ADMINISTRATE_USERS]}>
                            <AdminIdentityAttention />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/:orgId"
                    element={
                        <AdminRoute>
                            <OrganizationTeamsView />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/:orgId/user/:userId"
                    element={
                        <AdminRoute permissions={[P.WEVO.ACCESS.API.ADMINISTRATE_USERS]}>
                            <AdminUserDetailPage />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/admin/:orgId/sso"
                    element={
                        <AdminRoute>
                            <SsoAdmin />
                        </AdminRoute>
                    }
                />
                <Route
                    path="/profile"
                    element={
                        <AdminRoute>
                            <ProfilePage />
                        </AdminRoute>
                    }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    );
}

export default App;
